import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import pedidosService, { STORAGE_OPEN_PEDIDO } from "../../services/pedidosService";
import PedidoModal from "./PedidoModal";
import Banner from "../../components/ui/Banner";

const STATUS_BADGE = {
  Rascunho: "secondary",
  Aprovado: "primary",
  Enviado: "info",
  ParcialmenteRecebido: "warning",
  Recebido: "success",
  Cancelado: "dark",
};

const fmtD = (d) => {
  try {
    const s = (d || "").split("T")[0];
    return s ? new Date(`${s}T12:00:00`).toLocaleDateString("pt-BR") : "—";
  } catch { return "—"; }
};

export default function PedidosList({ onNavigate }) {
  const { condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("pedidos");
  const [idCondo, setIdCondo] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusList, setStatusList] = useState([]);

  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  useEffect(() => {
    pedidosService.meta().then((r) => setStatusList(r.data?.status || [])).catch(() => {});
  }, []);

  const carregar = useCallback(async () => {
    if (!idCondo) { setData([]); return; }
    setLoading(true); setError(null);
    try {
      const r = await pedidosService.listar({
        idCondominio: idCondo, status: filtroStatus, page, limit: 15,
      });
      setData(r.data || []);
      setPagination(r.pagination || { totalPages: 1 });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [idCondo, filtroStatus, page]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (!idCondo) return;
    const raw = sessionStorage.getItem(STORAGE_OPEN_PEDIDO);
    if (!raw) return;
    sessionStorage.removeItem(STORAGE_OPEN_PEDIDO);
    setEditId(Number(raw));
    setShowModal(true);
  }, [idCondo]);

  if (!idCondo) {
    return (
      <div className="page-header">
        <h1 className="page-title">Pedidos</h1>
        <p className="page-subtitle">Selecione um condomínio no topo.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header d-flex flex-wrap justify-content-between gap-2">
        <div>
          <h1 className="page-title">Pedidos de compra</h1>
          <p className="page-subtitle">Rascunhos, aprovação e itens editáveis — gere a partir do Estoque → Reposição</p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => { setEditId(null); setShowModal(true); }}>
            <i className="bi bi-plus-lg me-1" /> Novo pedido
          </button>
        )}
      </div>

      {error && <Banner tipo="danger" onClose={() => setError(null)}>{error}</Banner>}
      {okMsg && <Banner tipo="success" onClose={() => setOkMsg(null)}>{okMsg}</Banner>}

      <div className="d-flex gap-2 mb-3">
        <select className="form-select form-select-sm" style={{ maxWidth: 200 }}
          value={filtroStatus} onChange={(e) => { setFiltroStatus(e.target.value); setPage(1); }}>
          <option value="">Todos os status</option>
          {statusList.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Nº</th><th>Data</th><th>Fornecedor</th><th>Status</th>
                <th className="text-end">Itens</th><th />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-4 text-muted">Carregando…</td></tr>
              )}
              {!loading && data.map((p) => (
                <tr key={p.id}>
                  <td><code>{p.nrPedido || p.nrpedido}</code></td>
                  <td>{fmtD(p.createdAt || p.createdat)}</td>
                  <td>{p.nmFornecedor || "—"}</td>
                  <td>
                    <span className={`badge bg-${STATUS_BADGE[p.status] || "secondary"}`}>{p.status}</span>
                  </td>
                  <td className="text-end">{p.qtdItens ?? p.qtditens ?? 0}</td>
                  <td className="text-end">
                    <button type="button" className="btn btn-outline-secondary btn-sm"
                      onClick={() => { setEditId(p.id); setShowModal(true); }}>
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && !data.length && (
                <tr><td colSpan={6} className="text-center text-muted py-4">Nenhum pedido.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-center gap-2 mt-3">
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1}
            onClick={() => setPage((x) => x - 1)}>Anterior</button>
          <span className="align-self-center small">{page} / {pagination.totalPages}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((x) => x + 1)}>Próxima</button>
        </div>
      )}

      <PedidoModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSaved={() => { setOkMsg("Pedido salvo."); carregar(); }}
        pedidoId={editId}
        idCondominio={idCondo}
        canEdit={canEdit}
        saving={saving}
        setSaving={setSaving}
        onNavigateCompras={() => onNavigate && onNavigate("compras")}
      />
    </div>
  );
}
